import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { default as query } from 'query-string'
import type { ParsedQuery, StringifyOptions } from 'query-string'

export type UseQueryParamOptions = {
  /**
   * If true, replaces the current URL without adding to the history stack.
   */
  replace?: boolean
}

const queryStringSerializingOptions: StringifyOptions = {
  arrayFormat: 'bracket',
}

export type QueryParamValue =
  | string
  | string[]
  | (string | string[] | null | undefined)[]
  | null
  | undefined

type ParsedQueryParam = string | string[]
type QueryParamSetter<T extends string | string[]> = (
  newValue: T | undefined | ((prev: T | undefined) => T),
  options?: UseQueryParamOptions,
) => void

type ParsedQuerySetter = QueryParamSetter<ParsedQueryParam>

function isParsedQueryParam(value: unknown): value is ParsedQueryParam {
  return (
    typeof value === 'string' ||
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  )
}

/**
 * `useQueryParam<string[]>(key)`'s return type claims an array, but the
 * underlying parser (`query-string` with `arrayFormat: 'bracket'`) only
 * produces one for the `key[]=` URL form — a bare `key=value` parses to a
 * plain string at runtime instead. Coerce that shape back into an array
 * wherever a caller needs the `string[]` guarantee the type promised.
 * See PUBLISH-FRONTEND-2BA.
 */
export function toQueryParamArray(
  value: string[] | string | undefined,
): string[] {
  if (Array.isArray(value)) return value
  return value ? [value] : []
}

/**
 * This hook is used to manage query parameters in the URL.
 * It supports array with the same key.
 *
 * It's important to note that when setting a query param without `replace` option,
 * it will push a new entry to the history stack.
 * This should only happen if you want the history to be navigable.
 *
 * If you're interacting with a query param that should not influence forward/backward navigation,
 * you should use the `replace` option.
 *
 * TODO: Make `replace` option required to enforce intentional usage.
 *
 * @param param - The query parameter name to manage
 * @example
 * ```tsx
 * const [tab = 'defaultTab', setTab] = useQueryParam('tab')
 * const [tags, setChannels] = useQueryParam<string[]>('tags')
 *```
 */
export function useQueryParam<T extends string | string[] = string>(
  param: string,
): [T | undefined, QueryParamSetter<T>]
export function useQueryParam(
  param: string,
): [ParsedQueryParam | undefined, ParsedQuerySetter]
export function useQueryParam(
  param: string,
): [ParsedQueryParam | undefined, ParsedQuerySetter] {
  const location = useLocation()
  const navigate = useNavigate()

  const queryParams = useMemo(
    () => parseQueryParams(location.search),
    [location.search],
  )

  const rawValue = queryParams[param]
  const currentValue = isParsedQueryParam(rawValue) ? rawValue : undefined

  const setValue = useCallback(
    (
      newValue?:
        | ParsedQueryParam
        | ((prev: ParsedQueryParam | undefined) => ParsedQueryParam),
      options: UseQueryParamOptions = {},
    ): void => {
      const finalValue =
        typeof newValue === 'function' ? newValue(currentValue) : newValue

      if (finalValue === currentValue) return

      const newQueryParams = { ...queryParams }
      if (finalValue === undefined) {
        delete newQueryParams[param]
      } else {
        newQueryParams[param] = finalValue
      }

      const newSearch = serializeQueryParams(newQueryParams)
      const newLocation = { ...location, search: newSearch }

      if (options.replace) {
        navigate(newLocation, { replace: true })
      } else {
        navigate(newLocation)
      }
    },
    [currentValue, navigate, location, param, queryParams],
  )

  return [currentValue, setValue]
}

export function serializeQueryParams(params: {
  [x: string]: QueryParamValue
}): string {
  return query.stringify(params, queryStringSerializingOptions)
}

export function parseQueryParams(
  search: string,
): ParsedQuery<string | string[] | undefined> {
  return query.parse(search, queryStringSerializingOptions)
}
